import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  FlatList,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import Modal from '../components/Modal';
import { SafeAreaView } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Icon from 'react-native-vector-icons/Ionicons';
import Toast from 'react-native-toast-message';
import { StackScreenProps } from '@react-navigation/stack';
import {
  getStates,
  getDistricts,
  getCommunities,
  getEducations,
  getPreferences,
  savePreferences,
  getProfessions,
} from '../services/api';
import { THEME } from '../components';
import { RELIGIONS, JOB_TYPES, MARITAL_STATUSES, DIET_OPTIONS, SMOKING_OPTIONS, DRINKING_OPTIONS, INCOME_FILTER_OPTIONS } from '../constants';
import { useLanguage } from '../context/LanguageContext';
import type {
  RootStackParamList,
  StateResponse,
  CommunitySurnameResponse,
  EducationMasterResponse,
  ProfessionMasterResponse,
  UserPreference,
} from '../types';

type Props = StackScreenProps<RootStackParamList, 'Preferences'>;

const RELIGION_OPTIONS = RELIGIONS;
const MANGLIK_OPTIONS = [
  { label: 'Any', value: 'ANY' },
  { label: 'Yes', value: 'YES' },
  { label: 'No', value: 'NO' },
];
const AGE_PRESETS: { label: string; min: number; max: number }[] = [
  { label: '18–25', min: 18, max: 25 },
  { label: '26–30', min: 26, max: 30 },
  { label: '31–35', min: 31, max: 35 },
  { label: '36–40', min: 36, max: 40 },
  { label: '41+', min: 41, max: 60 },
];

// ── MultiSelectModal ──────────────────────────────────────────────────────────
interface MultiSelectModalProps {
  visible: boolean;
  title: string;
  items: { label: string; value: string }[];
  selected: string[];
  onClose: () => void;
  onToggle: (value: string) => void;
  onClearAll: () => void;
}

const MultiSelectModal: React.FC<MultiSelectModalProps> = ({
  visible, title, items, selected, onClose, onToggle, onClearAll,
}) => {
  const [search, setSearch] = useState('');
  const filtered = items.filter(i => i.label.toLowerCase().includes(search.toLowerCase()));

  const handleClose = () => { Keyboard.dismiss(); onClose(); setSearch(''); };

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={handleClose}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <TouchableOpacity style={msStyles.overlay} activeOpacity={1} onPress={handleClose}>
        <SafeAreaView style={msStyles.sheet} edges={['bottom']} onStartShouldSetResponder={() => true}>
          <View style={msStyles.header}>
            <Text style={msStyles.title}>{title}</Text>
            <View style={msStyles.headerRight}>
              {selected.length > 0 && (
                <>
                  <TouchableOpacity onPress={onClearAll} style={msStyles.clearBtn}>
                    <Text style={msStyles.clearBtnText}>Clear All</Text>
                  </TouchableOpacity>
                  <View style={msStyles.countBadge}>
                    <Text style={msStyles.countBadgeText}>{selected.length}</Text>
                  </View>
                </>
              )}
              <TouchableOpacity onPress={handleClose} style={msStyles.doneBtn}>
                <Text style={msStyles.doneBtnText}>Done</Text>
              </TouchableOpacity>
            </View>
          </View>
          <View style={msStyles.searchBox}>
            <Icon name="search-outline" size={18} color={THEME.textLight} />
            <TextInput
              style={msStyles.searchInput}
              value={search}
              onChangeText={setSearch}
              placeholder={`Search ${title}...`}
              placeholderTextColor={THEME.textLight}
              autoFocus
            />
            {search.length > 0 && (
              <TouchableOpacity onPress={() => setSearch('')}>
                <Icon name="close-circle" size={18} color={THEME.textLight} />
              </TouchableOpacity>
            )}
          </View>
          <FlatList
            data={filtered}
            keyExtractor={item => item.value}
            renderItem={({ item }) => {
              const active = selected.includes(item.value);
              return (
                <TouchableOpacity
                  style={[msStyles.option, active && msStyles.optionActive]}
                  onPress={() => onToggle(item.value)}>
                  <Text style={[msStyles.optionText, active && msStyles.optionTextActive]}>
                    {item.label}
                  </Text>
                  <View style={[msStyles.checkbox, active && msStyles.checkboxActive]}>
                    {active && <Icon name="checkmark" size={14} color="#fff" />}
                  </View>
                </TouchableOpacity>
              );
            }}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ paddingBottom: 32 }}
            ListEmptyComponent={
              <View style={msStyles.empty}>
                <Icon name="search-outline" size={32} color={THEME.border} />
                <Text style={msStyles.emptyText}>No results for "{search}"</Text>
              </View>
            }
          />
        </SafeAreaView>
      </TouchableOpacity>
      </KeyboardAvoidingView>
    </Modal>
  );
};

// ── MultiSelectButton ─────────────────────────────────────────────────────────
const MultiSelectButton: React.FC<{
  label: string;
  selected: string[];
  placeholder: string;
  onPress: () => void;
}> = ({ label, selected, placeholder, onPress }) => (
  <View style={styles.fieldSection}>
    <Text style={styles.fieldLabel}>{label}</Text>
    <TouchableOpacity style={[styles.msButton, selected.length > 0 && styles.msButtonActive]} onPress={onPress} activeOpacity={0.8}>
      <Text style={[styles.msButtonText, selected.length > 0 && styles.msButtonTextActive]} numberOfLines={2}>
        {selected.length === 0
          ? placeholder
          : selected.join(', ')}
      </Text>
      <View style={styles.msButtonRight}>
        {selected.length > 0 && (
          <View style={styles.msBadge}>
            <Text style={styles.msBadgeText}>{selected.length}</Text>
          </View>
        )}
        <Icon name="chevron-down" size={16} color={selected.length > 0 ? THEME.primary : THEME.textLight} />
      </View>
    </TouchableOpacity>
  </View>
);

// ── ChipGroup (multi-select) ──────────────────────────────────────────────────
const ChipGroup: React.FC<{
  items: { label: string; value: string }[];
  selected: string[];
  onToggle: (value: string) => void;
}> = ({ items, selected, onToggle }) => (
  <View style={styles.chipGrid}>
    {items.map(item => {
      const active = selected.includes(item.value);
      return (
        <TouchableOpacity
          key={item.value}
          style={[styles.chip, active && styles.chipActive]}
          onPress={() => onToggle(item.value)}
          activeOpacity={0.7}>
          <Text style={[styles.chipText, active && styles.chipTextActive]}>{item.label}</Text>
        </TouchableOpacity>
      );
    })}
  </View>
);

// ── SingleChipGroup ───────────────────────────────────────────────────────────
const SingleChipGroup: React.FC<{
  items: { label: string; value: string }[];
  selected: string;
  onSelect: (value: string) => void;
}> = ({ items, selected, onSelect }) => (
  <View style={styles.chipGrid}>
    {items.map(item => {
      const active = selected === item.value;
      return (
        <TouchableOpacity
          key={item.value}
          style={[styles.chip, active && styles.chipActive]}
          onPress={() => onSelect(item.value)}
          activeOpacity={0.7}>
          <Text style={[styles.chipText, active && styles.chipTextActive]}>{item.label}</Text>
        </TouchableOpacity>
      );
    })}
  </View>
);

// ── Screen ─────────────────────────────────────────────────────────────────────

const PreferencesScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Modal state
  const [stateModalVisible, setStateModalVisible] = useState(false);
  const [casteModalVisible, setCasteModalVisible] = useState(false);
  const [subgroupModalVisible, setSubgroupModalVisible] = useState(false);
  const [jobSectorModalVisible, setJobSectorModalVisible] = useState(false);
  const [jobModalVisible, setJobModalVisible] = useState(false);
  const [eduSubModalVisible, setEduSubModalVisible] = useState(false);

  // Preference state
  const [minAge, setMinAge] = useState<string>('');
  const [maxAge, setMaxAge] = useState<string>('');
  const [selectedReligion, setSelectedReligion] = useState<string>('');
  const [selectedMaritalStatuses, setSelectedMaritalStatuses] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [districtItems, setDistrictItems] = useState<{ label: string; value: string }[]>([]);
  const [districtModalVisible, setDistrictModalVisible] = useState(false);
  const [selectedCastes, setSelectedCastes] = useState<string[]>([]);
  const [selectedSubgroups, setSelectedSubgroups] = useState<string[]>([]);
  const [selectedEducations, setSelectedEducations] = useState<string[]>([]);
  const [selectedEduSubCategories, setSelectedEduSubCategories] = useState<string[]>([]);
  const [selectedJobTypes, setSelectedJobTypes] = useState<string[]>([]);
  const [selectedJobSectors, setSelectedJobSectors] = useState<string[]>([]);
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  const [selectedDiets, setSelectedDiets] = useState<string[]>([]);
  const [selectedSmoking, setSelectedSmoking] = useState<string[]>([]);
  const [selectedDrinking, setSelectedDrinking] = useState<string[]>([]);
  const [manglikPref, setManglikPref] = useState<string>('ANY');
  const [minAnnualIncome, setMinAnnualIncome] = useState<number>(0);

  // Master data
  const [states, setStates] = useState<StateResponse[]>([]);
  const [communities, setCommunities] = useState<CommunitySurnameResponse[]>([]);
  const [educations, setEducations] = useState<EducationMasterResponse[]>([]);
  const [professionSectors, setProfessionSectors] = useState<ProfessionMasterResponse[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [statesRes, commRes, eduRes, profRes, prefRes] = await Promise.allSettled([
      getStates(),
      getCommunities(),
      getEducations(),
      getProfessions(),
      getPreferences(),
    ]);

    if (statesRes.status === 'fulfilled') setStates(statesRes.value.data);
    if (commRes.status === 'fulfilled') setCommunities(commRes.value.data);
    if (eduRes.status === 'fulfilled') setEducations(eduRes.value.data);
    if (profRes.status === 'fulfilled') setProfessionSectors(profRes.value.data);

    if (prefRes.status === 'fulfilled') {
      const pref: UserPreference = prefRes.value.data;
      if (pref?.minAge) setMinAge(String(pref.minAge));
      if (pref?.maxAge) setMaxAge(String(pref.maxAge));
      const apiReligion = pref?.preferredReligions?.[0];
      if (apiReligion) {
        setSelectedReligion(apiReligion);
      } else {
        const stored = await AsyncStorage.getItem('pref_search_religion');
        setSelectedReligion(stored ?? '');
      }
      setSelectedMaritalStatuses(pref?.preferredMaritalStatuses ?? []);
      setSelectedStates(pref?.preferredStates ?? []);
      setSelectedDistricts(pref?.preferredDistricts ?? []);
      setSelectedCastes(pref?.preferredCastes ?? []);
      setSelectedSubgroups(pref?.preferredCasteSubTypes ?? []);
      setSelectedEducations(pref?.preferredEducations ?? []);
      setSelectedEduSubCategories(pref?.preferredEduSubCategories ?? []);
      setSelectedJobTypes(pref?.preferredJobTypes ?? []);
      setSelectedJobSectors(pref?.preferredJobSectors ?? []);
      setSelectedJobs(pref?.preferredJobs ?? []);
      setSelectedDiets(pref?.preferredDiets ?? []);
      setSelectedSmoking(pref?.preferredSmoking ?? []);
      setSelectedDrinking(pref?.preferredDrinking ?? []);
      if (pref?.preferredManglik === true) setManglikPref('YES');
      else if (pref?.preferredManglik === false) setManglikPref('NO');
      else setManglikPref('ANY');
      setMinAnnualIncome(pref?.minAnnualIncome ?? 0);
    }

    setLoading(false);
  };

  // Reload district list whenever selected states change
  useEffect(() => {
    if (selectedStates.length === 0 || states.length === 0) {
      setDistrictItems([]);
      setSelectedDistricts([]);
      return;
    }
    const stateCodes = selectedStates
      .map(name => states.find(s => s.en === name)?.stateCode)
      .filter(Boolean) as string[];
    Promise.all(stateCodes.map(code => getDistricts(code).then(r => r.data)))
      .then(results => {
        const all = results
          .flat()
          .map((d: any) => ({ label: d.en, value: d.en }))
          .filter((item: { value: string }, idx: number, arr: { value: string }[]) =>
            arr.findIndex(x => x.value === item.value) === idx,
          );
        setDistrictItems(all);
        // Drop any districts that no longer belong to the selected states
        const validSet = new Set(all.map((d: { value: string }) => d.value));
        setSelectedDistricts(prev => prev.filter(d => validSet.has(d)));
      })
      .catch(() => {});
  }, [selectedStates, states]);

  const toggle = (value: string, list: string[], setList: (v: string[]) => void) => {
    setList(list.includes(value) ? list.filter(i => i !== value) : [...list, value]);
  };

  const selectReligion = (value: string) => {
    const next = selectedReligion === value ? '' : value;
    setSelectedReligion(next);
    setSelectedSubgroups([]);
    if (next === '') {
      setSelectedCastes([]);
    } else {
      const validCastes = new Set(
        communities.filter(c => c.active && c.religion === next).map(c => c.community.en),
      );
      setSelectedCastes(prev => prev.filter(c => validCastes.has(c)));
    }
  };

  const toggleCaste = (value: string) => {
    const next = selectedCastes.includes(value)
      ? selectedCastes.filter(c => c !== value)
      : [...selectedCastes, value];
    setSelectedCastes(next);
    // Remove subgroups that no longer belong to any selected caste
    const validSubgroups = new Set(
      communities
        .filter(c => c.active && next.includes(c.community.en))
        .flatMap(c => c.subgroups.map(s => s.name.en)),
    );
    setSelectedSubgroups(prev => prev.filter(s => validSubgroups.has(s)));
  };

  const applyAgePreset = (preset: { min: number; max: number }) => {
    const already = minAge === String(preset.min) && maxAge === String(preset.max);
    if (already) {
      setMinAge(''); setMaxAge('');
    } else {
      setMinAge(String(preset.min)); setMaxAge(String(preset.max));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await savePreferences({
        minAge: minAge ? parseInt(minAge, 10) : null,
        maxAge: maxAge ? parseInt(maxAge, 10) : null,
        preferredReligions: selectedReligion ? [selectedReligion] : [],
        preferredMaritalStatuses: selectedMaritalStatuses,
        preferredStates: selectedStates,
        preferredDistricts: selectedDistricts,
        preferredCastes: selectedCastes,
        preferredCasteSubTypes: selectedSubgroups,
        preferredEducations: selectedEducations,
        preferredEduSubCategories: selectedEduSubCategories,
        preferredJobTypes: selectedJobTypes,
        preferredJobSectors: selectedJobSectors,
        preferredJobs: selectedJobs,
        preferredDiets: selectedDiets,
        preferredSmoking: selectedSmoking,
        preferredDrinking: selectedDrinking,
        preferredManglik:
          manglikPref === 'YES' ? true : manglikPref === 'NO' ? false : null,
        minAnnualIncome: minAnnualIncome > 0 ? minAnnualIncome : null,
      });
      if (selectedReligion) {
        await AsyncStorage.setItem('pref_search_religion', selectedReligion);
      } else {
        await AsyncStorage.removeItem('pref_search_religion');
      }
      Toast.show({ type: 'success', text1: 'Preferences saved!' });
      navigation.goBack();
    } catch (e) {
      Toast.show({ type: 'error', text1: 'Failed to save', text2: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={THEME.primary} />
      </View>
    );
  }

  const stateItems = states.map(s => ({ label: s.en, value: s.en }));
  const casteItems = communities
    .filter(c => c.active && (selectedReligion === '' || c.religion === selectedReligion))
    .map(c => ({ label: c.community.en, value: c.community.en }));

  const subgroupItems = communities
    .filter(c => c.active && selectedCastes.includes(c.community.en))
    .flatMap(c => c.subgroups.map(s => ({ label: s.name.en, value: s.name.en })))
    .filter((item, idx, arr) => arr.findIndex(x => x.value === item.value) === idx);
  const eduItems = educations
    .filter(e => e.active)
    .map(e => ({ label: e.educationLevel.en, value: e.educationLevel.en }));
  const jobItems = JOB_TYPES.map(j => ({ label: j, value: j }));
  const religionItems = RELIGION_OPTIONS.map(r => ({ label: r, value: r }));
  const maritalItems = MARITAL_STATUSES.map(m => ({ label: m, value: m }));

  const activeAgePreset = AGE_PRESETS.find(
    p => minAge === String(p.min) && maxAge === String(p.max),
  );

  return (
    <SafeAreaView style={styles.safeArea} edges={['bottom']}>
      <ScrollView style={styles.container} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">

        {/* Age Range */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('pref.ageRange', 'Age Range')}</Text>
          {/* Quick presets */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 12 }}>
            <View style={styles.presetRow}>
              {AGE_PRESETS.map(p => {
                const active = activeAgePreset?.label === p.label;
                return (
                  <TouchableOpacity
                    key={p.label}
                    style={[styles.presetChip, active && styles.presetChipActive]}
                    onPress={() => applyAgePreset(p)}
                    activeOpacity={0.8}>
                    <Text style={[styles.presetChipText, active && styles.presetChipTextActive]}>{p.label}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </ScrollView>
          {/* Manual input */}
          {(() => {
            const minVal = minAge ? parseInt(minAge, 10) : null;
            const maxVal = maxAge ? parseInt(maxAge, 10) : null;
            const ageInvalid = minVal !== null && maxVal !== null && minVal > maxVal;
            const handleMinBlur = () => {
              if (!minAge) return;
              let v = Math.max(18, Math.min(80, parseInt(minAge, 10)));
              setMinAge(String(v));
              if (maxAge && parseInt(maxAge, 10) < v) setMaxAge(String(v));
            };
            const handleMaxBlur = () => {
              if (!maxAge) return;
              let v = Math.max(18, Math.min(80, parseInt(maxAge, 10)));
              setMaxAge(String(v));
              if (minAge && parseInt(minAge, 10) > v) setMinAge(String(v));
            };
            return (
              <>
                <View style={styles.ageRow}>
                  <View style={styles.ageInputWrap}>
                    <Text style={styles.ageLabel}>{t('pref.minAge', 'Min Age')}</Text>
                    <TextInput
                      style={[styles.ageInput, ageInvalid && styles.ageInputError]}
                      keyboardType="numeric"
                      value={minAge}
                      onChangeText={v => setMinAge(v.replace(/\D/g, ''))}
                      onBlur={handleMinBlur}
                      placeholder="18"
                      placeholderTextColor={THEME.textLight}
                      maxLength={2}
                    />
                  </View>
                  <Text style={styles.ageDash}>—</Text>
                  <View style={styles.ageInputWrap}>
                    <Text style={styles.ageLabel}>{t('pref.maxAge', 'Max Age')}</Text>
                    <TextInput
                      style={[styles.ageInput, ageInvalid && styles.ageInputError]}
                      keyboardType="numeric"
                      value={maxAge}
                      onChangeText={v => setMaxAge(v.replace(/\D/g, ''))}
                      onBlur={handleMaxBlur}
                      placeholder="40"
                      placeholderTextColor={THEME.textLight}
                      maxLength={2}
                    />
                  </View>
                </View>
                {ageInvalid && <Text style={styles.ageErrorText}>Min age cannot exceed max age</Text>}
              </>
            );
          })()}
        </View>

        {/* Religion */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('pref.religion', 'Religion')}</Text>
          <Text style={styles.sectionHint}>{t('pref.anyHint', 'Leave empty to match any')}</Text>
          <SingleChipGroup
            items={religionItems}
            selected={selectedReligion}
            onSelect={selectReligion}
          />
        </View>

        {/* Caste — shown only after religion is selected */}
        {selectedReligion !== '' && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('pref.caste', 'Caste')}</Text>
            <Text style={styles.sectionHint}>{t('pref.anyHint', 'Leave empty to match any')}</Text>
            {casteItems.length > 0 ? (
              <MultiSelectButton
                label=""
                selected={selectedCastes}
                placeholder={t('pref.anyCaste', 'Any caste')}
                onPress={() => setCasteModalVisible(true)}
              />
            ) : (
              <Text style={styles.sectionHint}>No castes available for selected religion</Text>
            )}
          </View>
        )}

        {/* Community Subgroup — shown only after caste is selected */}
        {selectedCastes.length > 0 && subgroupItems.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('pref.subgroup', 'Community Subgroup')}</Text>
            <Text style={styles.sectionHint}>{t('pref.anyHint', 'Leave empty to match any')}</Text>
            <MultiSelectButton
              label=""
              selected={selectedSubgroups}
              placeholder="Any subgroup"
              onPress={() => setSubgroupModalVisible(true)}
            />
          </View>
        )}

        {/* Marital Status */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('pref.maritalStatus', 'Marital Status')}</Text>
          <Text style={styles.sectionHint}>{t('pref.anyHint', 'Leave empty to match any')}</Text>
          <ChipGroup
            items={maritalItems}
            selected={selectedMaritalStatuses}
            onToggle={v => toggle(v, selectedMaritalStatuses, setSelectedMaritalStatuses)}
          />
        </View>

        {/* State — searchable multi-select modal */}
        {stateItems.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('pref.state', 'State')}</Text>
            <Text style={styles.sectionHint}>{t('pref.anyHint', 'Leave empty to match any')}</Text>
            <MultiSelectButton
              label=""
              selected={selectedStates}
              placeholder={t('pref.anyState', 'Any state')}
              onPress={() => setStateModalVisible(true)}
            />
          </View>
        )}

        {/* District — shown only when states are selected */}
        {selectedStates.length > 0 && districtItems.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('field.district', 'District')}</Text>
            <Text style={styles.sectionHint}>{t('pref.anyHint', 'Leave empty to match any')}</Text>
            <MultiSelectButton
              label=""
              selected={selectedDistricts}
              placeholder="Any district"
              onPress={() => setDistrictModalVisible(true)}
            />
          </View>
        )}

        {/* Education Level */}
        {eduItems.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('pref.education', 'Education')}</Text>
            <Text style={styles.sectionHint}>{t('pref.anyHint', 'Leave empty to match any')}</Text>
            <ChipGroup
              items={eduItems}
              selected={selectedEducations}
              onToggle={v => { toggle(v, selectedEducations, setSelectedEducations); setSelectedEduSubCategories([]); }}
            />
          </View>
        )}

        {/* Education Specialization */}
        {educations.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('pref.eduSubCategory', 'Education Specialization')}</Text>
            <Text style={styles.sectionHint}>{t('pref.anyHint', 'Leave empty to match any')}</Text>
            <MultiSelectButton
              label={t('pref.eduSubCategory', 'Education Specialization')}
              selected={selectedEduSubCategories}
              placeholder="Any specialization"
              onPress={() => setEduSubModalVisible(true)}
            />
          </View>
        )}

        {/* Employment Type */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('pref.jobType', 'Employment Type')}</Text>
          <Text style={styles.sectionHint}>{t('pref.anyHint', 'Leave empty to match any')}</Text>
          <ChipGroup
            items={jobItems}
            selected={selectedJobTypes}
            onToggle={v => toggle(v, selectedJobTypes, setSelectedJobTypes)}
          />
        </View>

        {/* Job Sector */}
        {professionSectors.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('pref.jobSector', 'Job Sector')}</Text>
            <Text style={styles.sectionHint}>{t('pref.anyHint', 'Leave empty to match any')}</Text>
            <MultiSelectButton
              label={t('pref.jobSector', 'Job Sector')}
              selected={selectedJobSectors}
              placeholder="Any sector"
              onPress={() => setJobSectorModalVisible(true)}
            />
          </View>
        )}

        {/* Profession / Job */}
        {professionSectors.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('pref.profession', 'Profession')}</Text>
            <Text style={styles.sectionHint}>{t('pref.anyHint', 'Leave empty to match any')}</Text>
            <MultiSelectButton
              label={t('pref.profession', 'Profession')}
              selected={selectedJobs}
              placeholder="Any profession"
              onPress={() => setJobModalVisible(true)}
            />
          </View>
        )}

        {/* Manglik */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('field.manglik', 'Manglik')}</Text>
          <SingleChipGroup
            items={MANGLIK_OPTIONS}
            selected={manglikPref}
            onSelect={setManglikPref}
          />
        </View>

        {/* Minimum Annual Income */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('pref.minIncome', 'Minimum Annual Income')}</Text>
          <Text style={styles.sectionHint}>{t('pref.anyHint', 'Leave empty to match any')}</Text>
          <View style={styles.chipGrid}>
            {INCOME_FILTER_OPTIONS.map(opt => {
              const active = minAnnualIncome === opt.value;
              return (
                <TouchableOpacity
                  key={opt.value}
                  style={[styles.chip, active && styles.chipActive]}
                  onPress={() => setMinAnnualIncome(opt.value)}
                  activeOpacity={0.7}>
                  <Text style={[styles.chipText, active && styles.chipTextActive]}>{opt.label}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* Diet */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('pref.diet', 'Diet')}</Text>
          <Text style={styles.sectionHint}>{t('pref.anyHint', 'Leave empty to match any')}</Text>
          <ChipGroup
            items={DIET_OPTIONS.map(d => ({ label: d, value: d }))}
            selected={selectedDiets}
            onToggle={v => toggle(v, selectedDiets, setSelectedDiets)}
          />
        </View>

        {/* Smoking */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('pref.smoking', 'Smoking')}</Text>
          <Text style={styles.sectionHint}>{t('pref.anyHint', 'Leave empty to match any')}</Text>
          <ChipGroup
            items={SMOKING_OPTIONS.map(s => ({ label: s, value: s }))}
            selected={selectedSmoking}
            onToggle={v => toggle(v, selectedSmoking, setSelectedSmoking)}
          />
        </View>

        {/* Drinking */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('pref.drinking', 'Drinking')}</Text>
          <Text style={styles.sectionHint}>{t('pref.anyHint', 'Leave empty to match any')}</Text>
          <ChipGroup
            items={DRINKING_OPTIONS.map(d => ({ label: d, value: d }))}
            selected={selectedDrinking}
            onToggle={v => toggle(v, selectedDrinking, setSelectedDrinking)}
          />
        </View>

        {/* Save button — inside scroll, with extra bottom padding for safe area */}
        <TouchableOpacity
          style={[styles.saveBtn, saving && styles.saveBtnDisabled]}
          onPress={handleSave}
          disabled={saving}
          activeOpacity={0.8}>
          {saving ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.saveBtnText}>{t('btn.savePreferences', 'Save Preferences')}</Text>
          )}
        </TouchableOpacity>

      </ScrollView>

      {/* Multi-select modals */}
      <MultiSelectModal
        visible={stateModalVisible}
        title={t('pref.state', 'State')}
        items={stateItems}
        selected={selectedStates}
        onClose={() => setStateModalVisible(false)}
        onToggle={v => toggle(v, selectedStates, setSelectedStates)}
        onClearAll={() => setSelectedStates([])}
      />
      <MultiSelectModal
        visible={districtModalVisible}
        title={t('field.district', 'District')}
        items={districtItems}
        selected={selectedDistricts}
        onClose={() => setDistrictModalVisible(false)}
        onToggle={v => toggle(v, selectedDistricts, setSelectedDistricts)}
        onClearAll={() => setSelectedDistricts([])}
      />
      <MultiSelectModal
        visible={casteModalVisible}
        title={t('pref.caste', 'Caste')}
        items={casteItems}
        selected={selectedCastes}
        onClose={() => setCasteModalVisible(false)}
        onToggle={toggleCaste}
        onClearAll={() => { setSelectedCastes([]); setSelectedSubgroups([]); }}
      />
      <MultiSelectModal
        visible={subgroupModalVisible}
        title={t('pref.subgroup', 'Subcaste')}
        items={subgroupItems}
        selected={selectedSubgroups}
        onClose={() => setSubgroupModalVisible(false)}
        onToggle={v => toggle(v, selectedSubgroups, setSelectedSubgroups)}
        onClearAll={() => setSelectedSubgroups([])}
      />
      <MultiSelectModal
        visible={eduSubModalVisible}
        title={t('pref.eduSubCategory', 'Education Specialization')}
        items={(selectedEducations.length > 0
          ? educations.filter(e => selectedEducations.includes(e.educationLevel.en))
          : educations
        ).flatMap(e => e.subCategories.map(s => ({ label: s.en, value: s.en })))
          .filter((item, idx, arr) => arr.findIndex(x => x.value === item.value) === idx)}
        selected={selectedEduSubCategories}
        onClose={() => setEduSubModalVisible(false)}
        onToggle={v => toggle(v, selectedEduSubCategories, setSelectedEduSubCategories)}
        onClearAll={() => setSelectedEduSubCategories([])}
      />
      <MultiSelectModal
        visible={jobSectorModalVisible}
        title={t('pref.jobSector', 'Job Sector')}
        items={professionSectors.map(s => ({ label: s.sector.en, value: s.sector.en }))}
        selected={selectedJobSectors}
        onClose={() => setJobSectorModalVisible(false)}
        onToggle={v => {
          toggle(v, selectedJobSectors, setSelectedJobSectors);
          setSelectedJobs([]);
        }}
        onClearAll={() => { setSelectedJobSectors([]); setSelectedJobs([]); }}
      />
      <MultiSelectModal
        visible={jobModalVisible}
        title={t('pref.profession', 'Profession')}
        items={(selectedJobSectors.length > 0
          ? professionSectors.filter(s => selectedJobSectors.includes(s.sector.en))
          : professionSectors
        ).flatMap(s => s.professions.map(p => ({ label: p.en, value: p.en })))}
        selected={selectedJobs}
        onClose={() => setJobModalVisible(false)}
        onToggle={v => toggle(v, selectedJobs, setSelectedJobs)}
        onClearAll={() => setSelectedJobs([])}
      />
    </SafeAreaView>
  );
};

export default PreferencesScreen;

// ── MultiSelectModal styles ───────────────────────────────────────────────────
const msStyles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
  sheet: {
    backgroundColor: THEME.bg, borderTopLeftRadius: 24, borderTopRightRadius: 24,
    maxHeight: '80%',
  },
  header: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: 20, paddingTop: 20, paddingBottom: 14,
    borderBottomWidth: 1, borderColor: THEME.border,
  },
  title: { fontSize: 17, fontWeight: '800', color: THEME.text },
  headerRight: { flexDirection: 'row', alignItems: 'center' },
  countBadge: {
    backgroundColor: THEME.primary, borderRadius: 10,
    minWidth: 22, height: 22, alignItems: 'center', justifyContent: 'center',
    paddingHorizontal: 6,
  },
  countBadgeText: { color: '#fff', fontSize: 12, fontWeight: '800' },
  clearBtn: {
    marginLeft: 8, paddingHorizontal: 12, paddingVertical: 6,
    borderRadius: 20, borderWidth: 1.5, borderColor: THEME.primary,
  },
  clearBtnText: { color: THEME.primary, fontSize: 13, fontWeight: '700' },
  doneBtn: {
    marginLeft: 10, paddingHorizontal: 14, paddingVertical: 6,
    backgroundColor: THEME.primary, borderRadius: 20,
  },
  doneBtnText: { color: '#fff', fontSize: 13, fontWeight: '800' },
  searchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    marginHorizontal: 16, marginVertical: 12,
    borderWidth: 1.5, borderColor: THEME.border, borderRadius: 12,
    paddingHorizontal: 12, paddingVertical: 10, backgroundColor: THEME.card,
  },
  searchInput: { flex: 1, fontSize: 15, color: THEME.text, padding: 0 },
  option: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 20, paddingVertical: 14,
    borderBottomWidth: 1, borderColor: THEME.border,
  },
  optionActive: { backgroundColor: '#FAE5E5' },
  optionText: { fontSize: 15, color: THEME.text, flex: 1, marginRight: 12 },
  optionTextActive: { color: THEME.primary, fontWeight: '700' },
  checkbox: {
    width: 22, height: 22, borderRadius: 6, borderWidth: 2,
    borderColor: THEME.border, alignItems: 'center', justifyContent: 'center',
    backgroundColor: THEME.bg,
  },
  checkboxActive: { backgroundColor: THEME.primary, borderColor: THEME.primary },
  empty: { alignItems: 'center', justifyContent: 'center', paddingVertical: 48, gap: 12 },
  emptyText: { fontSize: 14, color: THEME.textLight, textAlign: 'center' },
});

// ── Screen styles ─────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: THEME.bg },
  container: { flex: 1, backgroundColor: THEME.bg },
  content: { padding: 16, paddingBottom: 32 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: THEME.bg },

  section: {
    backgroundColor: THEME.card,
    borderRadius: 16,
    padding: 16,
    marginBottom: 14,
    shadowColor: '#000',
    shadowOpacity: 0.04,
    shadowRadius: 6,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: THEME.text,
    marginBottom: 4,
  },
  sectionHint: {
    fontSize: 12,
    color: THEME.textLight,
    marginBottom: 10,
  },
  fieldSection: { marginTop: 4 },
  fieldLabel: { fontSize: 13, color: THEME.textLight, marginBottom: 6, fontWeight: '600' },

  chipGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: THEME.border,
    backgroundColor: THEME.bg,
  },
  chipActive: {
    backgroundColor: THEME.primary,
    borderColor: THEME.primary,
  },
  chipText: {
    fontSize: 13,
    fontWeight: '600',
    color: THEME.textLight,
  },
  chipTextActive: {
    color: '#fff',
  },

  // Age presets
  presetRow: { flexDirection: 'row', gap: 8 },
  presetChip: {
    paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20,
    borderWidth: 1.5, borderColor: THEME.border, backgroundColor: THEME.bg,
  },
  presetChipActive: { backgroundColor: THEME.primary, borderColor: THEME.primary },
  presetChipText: { fontSize: 13, fontWeight: '700', color: THEME.textLight },
  presetChipTextActive: { color: '#fff' },

  // Age manual inputs
  ageRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginTop: 4,
    gap: 12,
  },
  ageInputWrap: { flex: 1 },
  ageLabel: {
    fontSize: 12,
    color: THEME.textLight,
    marginBottom: 6,
    fontWeight: '600',
  },
  ageInput: {
    borderWidth: 1.5,
    borderColor: THEME.border,
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 16,
    fontWeight: '700',
    color: THEME.text,
    backgroundColor: THEME.bg,
    textAlign: 'center',
  },
  ageInputError: { borderColor: '#E53E3E' },
  ageErrorText: { fontSize: 12, color: '#E53E3E', fontWeight: '600', marginTop: 6 },
  ageDash: {
    fontSize: 20,
    color: THEME.textLight,
    paddingBottom: 10,
  },

  // MultiSelectButton
  msButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    borderWidth: 1.5, borderColor: THEME.border, borderRadius: 12,
    paddingHorizontal: 14, paddingVertical: 13, backgroundColor: THEME.bg,
    marginTop: 4,
  },
  msButtonActive: { borderColor: THEME.primary },
  msButtonText: { fontSize: 14, color: THEME.textLight, flex: 1, marginRight: 8 },
  msButtonTextActive: { color: THEME.primary, fontWeight: '600' },
  msButtonRight: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  msBadge: {
    backgroundColor: THEME.primary, borderRadius: 10,
    minWidth: 20, height: 20, alignItems: 'center', justifyContent: 'center',
    paddingHorizontal: 5,
  },
  msBadgeText: { color: '#fff', fontSize: 11, fontWeight: '800' },

  saveBtn: {
    backgroundColor: THEME.primary,
    borderRadius: 14,
    paddingVertical: 16,
    alignItems: 'center',
    marginTop: 8,
  },
  saveBtnDisabled: { opacity: 0.6 },
  saveBtnText: { color: '#fff', fontSize: 16, fontWeight: '800' },
});
